import { useEffect, useState } from 'react'

type Unit = 'min' | 'h' | 'd'

const FACTOR: Record<Unit, number> = { min: 1, h: 60, d: 1440 }
const UNIT_LABELS: Record<Unit, string> = { min: 'minutos', h: 'horas', d: 'dias' }

/** Maior unidade que representa o valor sem fração: 1440 -> 1 dia, 90 -> 90 min. */
function bestUnit(minutes: number): Unit {
    if (minutes > 0 && minutes % 1440 === 0) return 'd'
    if (minutes > 0 && minutes % 60 === 0) return 'h'
    return 'min'
}

/**
 * Tempo configurável em minutos, horas ou dias. O valor guardado é sempre em
 * minutos (é o que o motor lê); a unidade é só da tela.
 */
export default function DurationInput({
    id,
    value,
    onChange,
    min = 0,
}: {
    id?: string
    value: number | null | undefined
    onChange: (minutes: number) => void
    min?: number
}) {
    const minutes = Math.max(0, Number(value) || 0)
    const [unit, setUnit] = useState<Unit>(bestUnit(minutes))
    const [amount, setAmount] = useState<string>(minutes ? String(minutes / FACTOR[bestUnit(minutes)]) : '')

    // Valor trocado de fora (outro passo selecionado): reposiciona unidade e número.
    useEffect(() => {
        const current = Number(amount) * FACTOR[unit]
        if (current !== minutes) {
            const next = bestUnit(minutes)
            setUnit(next)
            setAmount(minutes ? String(minutes / FACTOR[next]) : '')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minutes])

    const emit = (nextAmount: string, nextUnit: Unit) => {
        const parsed = Math.max(min, Math.round((Number(nextAmount) || 0) * FACTOR[nextUnit]))
        onChange(parsed)
    }

    return (
        <div className="join w-full">
            <input
                id={id}
                type="number"
                min={0}
                step="any"
                className="input input-bordered input-sm join-item w-full"
                value={amount}
                placeholder="0"
                onChange={e => {
                    setAmount(e.target.value)
                    emit(e.target.value, unit)
                }}
            />
            <select
                className="select select-bordered select-sm join-item"
                value={unit}
                onChange={e => {
                    const nextUnit = e.target.value as Unit
                    setUnit(nextUnit)
                    emit(amount, nextUnit)
                }}
            >
                {(Object.keys(UNIT_LABELS) as Unit[]).map(u => (
                    <option key={u} value={u}>
                        {UNIT_LABELS[u]}
                    </option>
                ))}
            </select>
        </div>
    )
}
